import type { PolyPt } from '@roomtreatment/floorplan-core';

export type { PolyPt };

export function pointInPolygon(p: PolyPt, poly: PolyPt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    if ((a.y > p.y) !== (b.y > p.y)) {
      const x = a.x + ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y);
      if (p.x < x) inside = !inside;
    }
  }
  return inside;
}

function projectToSegment(p: PolyPt, a: PolyPt, b: PolyPt): PolyPt {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return { x: a.x, y: a.y };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * dx, y: a.y + t * dy };
}

export function clampPointToPolygon(p: PolyPt, poly: PolyPt[]): PolyPt {
  if (poly.length < 3 || pointInPolygon(p, poly)) return p;
  let best = p;
  let bestD = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const q = projectToSegment(p, poly[i]!, poly[(i + 1) % poly.length]!);
    const d = Math.hypot(q.x - p.x, q.y - p.y);
    if (d < bestD) {
      bestD = d;
      best = q;
    }
  }
  return best;
}

export function clampBoxToRoom(
  x: number,
  z: number,
  hx: number,
  hz: number,
  poly: PolyPt[],
): { x: number; z: number } {
  const c0 = clampPointToPolygon({ x, y: z }, poly);
  let cx = c0.x;
  let cz = c0.y;

  for (let it = 0; it < 3; it++) {
    const corners: Array<[number, number]> = [
      [cx - hx, cz - hz],
      [cx + hx, cz - hz],
      [cx + hx, cz + hz],
      [cx - hx, cz + hz],
    ];
    let dx = 0;
    let dz = 0;
    let anyOut = false;
    for (const [px, pz] of corners) {
      if (pointInPolygon({ x: px, y: pz }, poly)) continue;
      anyOut = true;
      const q = clampPointToPolygon({ x: px, y: pz }, poly);
      const sx = q.x - px;
      const sz = q.y - pz;
      if (Math.abs(sx) > Math.abs(dx)) dx = sx;
      if (Math.abs(sz) > Math.abs(dz)) dz = sz;
    }
    if (!anyOut) break;
    cx += dx;
    cz += dz;
  }
  return { x: cx, z: cz };
}
